import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import Logo from "./Logo";
import { nav, company } from "../data/mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:block bg-slate-900 text-slate-300 text-sm">
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-10">
          <a href={company.phoneHref} className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone className="h-3.5 w-3.5 text-blue-400" />
            {company.phone}
          </a>
          <div className="flex items-center gap-6">
            <a href={company.emailHref} className="hover:text-white transition-colors">
              {company.email}
            </a>
            <span className="text-slate-500">{company.address.line1}, {company.address.line2}</span>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "glass shadow-md border-b border-slate-200/60" : "bg-white/95 border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-[68px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    isActive ? "text-blue-600" : "text-slate-700 hover:text-blue-600"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-blue-600 transition-all duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/admin/login">
              <Button variant="ghost" className="text-slate-700 font-semibold">Login</Button>
            </Link>
            <a href={company.bookingUrl} target="_blank" rel="noreferrer">
              <Button className="brand-gradient text-white font-semibold shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-shadow group">
                Schedule a Call
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </a>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-slate-800">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <div className="flex items-center justify-between px-5 h-[68px] border-b">
                  <Logo />
                  <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <nav className="flex flex-col p-4 gap-1">
                  {nav.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === "/"}
                      className={({ isActive }) =>
                        `px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                          isActive ? "bg-blue-50 text-blue-600" : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                  <a href={company.bookingUrl} target="_blank" rel="noreferrer" className="mt-4">
                    <Button className="w-full brand-gradient text-white font-semibold">
                      Schedule a Call
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </a>
                  <a href={company.phoneHref} className="mt-2">
                    <Button variant="outline" className="w-full font-semibold">
                      <Phone className="mr-2 h-4 w-4" /> {company.phone}
                    </Button>
                  </a>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
